import { get } from 'lodash';
import { NEW_TAB_PROPS } from 'modules/projectSettings/constants';
import type { ProjectSettingsFormSchemaType } from 'modules/projectSettings/contexts/ProjectSettingsFormProvider';
import { AnchorHTMLAttributes } from 'react';
import { useFormState } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { FormField, FormFieldProps } from 'ui/formField';
import { Code, colors, fontWeights } from 'ui/theme';
import { StyledWebLink } from '../Styled';

const StyledLink = ({ href, children }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <StyledWebLink href={href} {...NEW_TAB_PROPS}>
    {children}
  </StyledWebLink>
);

type MarkdownDescriptionProps = {
  id: string;
  linkText?: string;
  markdownText: string;
  supportUrl?: string;
};

export const MarkdownDescription = ({
  id,
  linkText = 'Learn more',
  markdownText,
  supportUrl,
}: MarkdownDescriptionProps) => (
  <span data-test={id}>
    <ReactMarkdown components={{ a: StyledLink, code: Code }} linkTarget="_blank">
      {supportUrl ? markdownText.concat(` [${linkText}](${supportUrl})`) : markdownText}
    </ReactMarkdown>
  </span>
);

export const StyledSpan = styled.span`
  color: ${colors.textSecondary};
  font-weight: ${fontWeights.medium};
`;

export const OptionalLabelText = ({ text }: { text: string }) => (
  <>
    <span>{text}</span>&nbsp;<StyledSpan>(optional)</StyledSpan>
  </>
);

type Props = {
  name: keyof ProjectSettingsFormSchemaType;
  children: React.ReactNode;
} & FormFieldProps;

export const ConnectedFormField = ({ labelText, name, children, ...rest }: Props) => {
  const { errors } = useFormState<ProjectSettingsFormSchemaType>({ name });
  const { message } = get(errors, name, { message: '' });

  return (
    <FormField
      {...rest}
      labelText={labelText}
      inputId={name}
      errorText={message}
      hasError={Boolean(message)}
    >
      {children}
    </FormField>
  );
};
